import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  GlobalStyles,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';

import { SWLoadingButton } from '@/components/LoadingButton';
import { PhoneNumberInput } from '@/components/PhoneNumberInput';
import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';
import { Unit } from '@/Units/types';
import { naturalCompare } from '@/util';

import { ISubscibersBody } from '../types';
import { IFormData } from './types';

const useStyles = makeStyles((theme) => ({
  paper: {
    minHeight: 560,
    borderRadius: 10,
  },
  input: {
    margin: theme.spacing(1),
    marginTop: '10px',
  },
}));

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: Function;
  type: 'ADD' | 'EDIT';
  loading?: boolean;
  units: Unit[];
  editData?: IFormData;
  addTenant: Function;
};

export const FormDialogSubscriber = ({
  open,
  type,
  onClose,
  onSubmit,
  loading: _loading = false, // destructuring alias to avoid unused variable warning
  units,
  editData,
  addTenant,
}: Props) => {
  const classes = useStyles();
  const [errValUnit, setErrValUnit] = React.useState(false);
  const [errValEmail, setErrValEmail] = React.useState({ email: false, emailErrMsj: '' });
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [openWarning, setOpenWarning] = React.useState<boolean>(false);
  const [warningEdit, setWarningEdit] = React.useState({ email: false, name: false });

  const [formData, setFormData] = React.useState<IFormData>({
    id: '',
    unit: { label: '', id: '' },
    email: '',
    fist_name: '',
    last_name: '',
    phone_number: '',
  });

  React.useEffect(() => {
    if (editData) setFormData(editData);
  }, [editData]);

  const transformUnits = (units: Unit[]): { label: string; id: string }[] => {
    return [...units]
      .sort((a, b) => naturalCompare(a.name, b.name))
      .map((unit) => ({
        label: unit.name,
        id: unit.id,
      }));
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    if (formData.email === '')
      setErrValEmail({ ...errValEmail, email: true, emailErrMsj: 'Required' });
    if (formData.unit.id === '') setErrValUnit(true);
    if (formData.email !== '' && !validateEmail(formData.email))
      setErrValEmail({ ...errValEmail, email: true, emailErrMsj: 'Invalid Email Format' });
    if (formData.email !== '' && formData.unit.id !== '' && validateEmail(formData.email)) {
      setIsLoading(true);

      let data: ISubscibersBody = {
        subscribers: [
          {
            id: formData.id,
            email: formData.email,
            first_name: formData.fist_name,
            last_name: formData.last_name,
            phone_number: formData.phone_number,
            unit_subscriptions: [
              {
                unit_id: formData.unit.id,
              },
            ],
          },
        ],
      };
      if (type === 'EDIT') {
        if (warningEdit.email && warningEdit.name) {
          setFormData({
            id: '',
            unit: { label: '', id: '' },
            email: '',
            fist_name: '',
            last_name: '',
            phone_number: '',
          });
          setIsLoading(false);
          onClose();
          setWarningEdit({ email: false, name: false });
          return setOpenWarning(true);
        }
        await onSubmit(data.subscribers[0], {
          unit_id: formData.unit.id,
          unit_name: formData.unit.label,
        });
      }

      if (type === 'ADD')
        await onSubmit(data, { unit_id: formData.unit.id, unit_name: formData.unit.label });
      setFormData({
        id: '',
        unit: { label: '', id: '' },
        email: '',
        fist_name: '',
        last_name: '',
        phone_number: '',
      });
      setWarningEdit({ email: false, name: false });
      setIsLoading(false);
      onClose();
    }
  };

  const handleClose = () => {
    // Reset form data to initial state
    setFormData({
      id: '',
      unit: { label: '', id: '' },
      email: '',
      fist_name: '',
      last_name: '',
      phone_number: '',
    });

    // Reset error states
    setErrValUnit(false);
    setErrValEmail({ email: false, emailErrMsj: '' });

    // Reset warning states
    setWarningEdit({ email: false, name: false });

    // Call the original onClose prop
    onClose();
  };

  const transformedUnits = transformUnits(units);
  return (
    <>
      <GlobalStyles
        styles={{
          '.MuiOutlinedInput-root': {
            borderRadius: '14px !important',
            minHeight: '45px !important',
            padding: '0 !important',
          },
          '.MuiOutlinedInput-input': {
            padding: '8px 12px !important',
            lineHeight: '1.5em',
          },
          '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 2px rgba(71,144,204,0.2) !important',
          },
        }}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth={'sm'}
        fullWidth
        classes={{
          paper: classes.paper,
        }}
      >
        <DialogTitle
          id="form-dialog-title"
          sx={{ margin: 'auto', textAlign: 'center', paddingTop: 5 }}
        >
          <Stack direction={'column'}>
            <Box sx={{ fontSize: '42px', fontWeight: 'bold' }}>
              {type === 'EDIT' ? 'Edit' : 'Create'} tenant
            </Box>
            <Box sx={{ fontSize: '18px' }}>
              {type === 'EDIT'
                ? 'Update this tenant’s information below'
                : 'Add a new tenant and assign them to a unit'}
            </Box>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey', marginTop: 2 }} />
        <DialogContent sx={{ margin: 3 }}>
          <Stack spacing={3}>
            <Box>
              <Typography fontSize="16px" fontWeight={400} mb={1}>
                Unit Name *
              </Typography>
              <Autocomplete
                value={formData.unit}
                onChange={(event, newValue) => {
                  const value = newValue ? newValue : formData.unit;
                  setFormData({ ...formData, unit: value });
                }}
                options={transformedUnits}
                renderInput={(params) => (
                  <TextField
                    error={errValUnit}
                    {...params}
                    variant="outlined"
                    placeholder="Select unit"
                  />
                )}
              />
              {errValUnit && (
                <Typography
                  fontSize="14px"
                  fontWeight={400}
                  variant="body2"
                  sx={{ color: '#e53935', mt: 0.5 }}
                >
                  Required
                </Typography>
              )}
            </Box>

            <Box>
              <Typography fontSize="16px" fontWeight={400} mb={1}>
                Email Address *
              </Typography>
              <TextField
                error={errValEmail.email}
                margin="none"
                type="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={(e) => {
                  setWarningEdit({ ...warningEdit, email: true });
                  setFormData({ ...formData, email: e.target.value });
                }}
                variant="outlined"
                fullWidth
              />
              {errValEmail.emailErrMsj && (
                <Typography
                  fontSize="14px"
                  fontWeight={400}
                  variant="body2"
                  sx={{ color: '#e53935', mt: 0.5 }}
                >
                  {errValEmail.emailErrMsj}
                </Typography>
              )}
            </Box>

            <PhoneNumberInput
              label="Phone Number (optional)"
              value={formData.phone_number || ''}
              onChange={(fullNumber) => {
                setFormData({ ...formData, phone_number: fullNumber });
              }}
            />

            <Box>
              <Typography fontSize="16px" fontWeight={400} mb={1}>
                First Name (optional)
              </Typography>
              <TextField
                margin="none"
                placeholder="Enter first name"
                value={formData.fist_name}
                onChange={(e) => {
                  setWarningEdit({ ...warningEdit, name: true });
                  setFormData({ ...formData, fist_name: e.target.value });
                }}
                variant="outlined"
                fullWidth
              />
            </Box>

            <Box>
              <Typography fontSize="16px" fontWeight={400} mb={1}>
                Last Name (optional)
              </Typography>
              <TextField
                margin="none"
                placeholder="Enter last name"
                value={formData.last_name}
                onChange={(e) => {
                  setWarningEdit({ ...warningEdit, name: true });
                  setFormData({ ...formData, last_name: e.target.value });
                }}
                variant="outlined"
                fullWidth
              />
            </Box>
          </Stack>
        </DialogContent>
        <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey' }} />
        <DialogActions sx={{ justifyContent: 'space-between !important', margin: 2 }}>
          <SWLoadingButtonOutlined onClick={handleClose}>Cancel</SWLoadingButtonOutlined>
          <SWLoadingButton onClick={() => handleSubmit()} loading={isLoading}>
            {type === 'EDIT' ? 'Done' : 'Create'}
          </SWLoadingButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openWarning}
        onClose={() => setOpenWarning(false)}
        keepMounted
        aria-describedby="alert-dialog-slide-description"
        sx={{ textAlign: 'center' }}
      >
        <Stack justifyContent={'center'} margin={5} gap={2}>
          <img
            src="/img/warningIcon.png"
            height={56}
            width={56}
            style={{ margin: 'auto' }}
            alt="timeLogo"
          />
          <Typography fontSize={24} fontWeight={700}>
            New Tenant Recommended
          </Typography>
          <Typography fontSize={16} fontWeight={400}>
            To prevent issues, create a new tenant and delete the old one. Editing existing details
            may cause billing discrepancies.
          </Typography>
        </Stack>

        <Stack direction={'row'} justifyContent={'center'} margin={1} gap={1}>
          <SWLoadingButtonOutlined onClick={() => setOpenWarning(false)}>
            Cancel
          </SWLoadingButtonOutlined>
          <SWLoadingButton
            loading={isLoading}
            onClick={() => {
              setOpenWarning(false);
              addTenant(true);
            }}
          >
            Create new tenant
          </SWLoadingButton>
        </Stack>
      </Dialog>
    </>
  );
};
